import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { StateBadge } from '@/components/platform/StateBadge';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Tooltip } from '@/components/ui/Tooltip';
import { useToast } from '@/components/ui/Toast';
import { useAsyncData } from '@/hooks';
import {
  FIELD_SENSITIVITIES,
  FORM_FIELD_TYPES,
  INSTITUTION_TYPES,
  type ChangeEvent,
  type FieldSensitivity,
  type FormField,
  type FormFieldType,
  type FormSchema,
  type FormSchemaDraftRequest,
  type FormSection,
  type LifecycleStatus,
  type ValidationReport,
} from '@/types/config-governance';
import {
  eligibleConditionTargets,
  eligibleSectionConditionTargets,
  evaluateVisibility,
} from '@/pages/platform/form-visibility';
import { HistoryTimeline, LifecycleTransitions } from '@/pages/platform/GovernanceLifecycle';
import styles from '@/pages/platform/platform-pages.module.css';
import gov from '@/pages/platform/governance.module.css';

/**
 * The CFG-11 schema editor — one component, two consoles.
 *
 * The platform console and the institution admin console both need the same
 * builder: sections and fields, a live preview, the lifecycle, and the
 * protected-floor locks. Everything that differs between the two shells is
 * injected rather than hardcoded here:
 *
 *  - `api` — the CRUD+lifecycle calls. The platform gives it plain
 *    formSchemasApi; an institution administrator gets institutionFormSchemasApi
 *    bound to their institution. The editor never passes `institutionId` itself —
 *    it is not its business where the record lives, and on the institution side
 *    the server refuses a provider-supplied one.
 *  - navigation — where the register is and how detail pages are addressed, so
 *    both consoles show their own breadcrumbs and land their redirects in the
 *    right shell.
 *
 * PROTECTED FIELDS ARE THE PLATFORM FLOOR. On a platform template the lock is
 * authorship — this template's protected fields become the floor every derived
 * institution version must carry unchanged. On an institution schema the lock
 * is a wall: code, type, required and sensitivity are disabled in the editor
 * and the row cannot be removed, mirroring the validator's rule (label and
 * helpText may still be localised). The tooltip on the lock says exactly this,
 * because a disabled control with no stated why teaches operators that the
 * console is broken.
 *
 * CONDITIONS ONLY POINT BACKWARDS. The condition pickers offer strictly
 * earlier fields (and, for sections, fields of earlier sections), because the
 * server refuses forward references — the renderer resolves top-down in one
 * pass. The preview pane resolves visibility with the same pure function the
 * tests pin, so what the operator sees toggling answers is what an applicant
 * will get.
 */

export interface FormSchemaEditorApi {
  update: (id: string, body: Partial<FormSchemaDraftRequest>) => Promise<FormSchema>;
  remove: (id: string) => Promise<void>;
  create: (body: FormSchemaDraftRequest) => Promise<FormSchema>;
  validate: (id: string) => Promise<ValidationReport>;
  transition: (id: string, toStatus: LifecycleStatus, reason?: string) => Promise<FormSchema>;
}

interface FormSchemaEditorProps {
  schema: FormSchema;
  replace: (next: FormSchema) => void;
  historyState: ReturnType<typeof useAsyncData<ChangeEvent[]>>;
  onDeleted: () => void;
  toast: ReturnType<typeof useToast>;
  api: FormSchemaEditorApi;
  /** Builds the URL of a schema detail page (for a freshly drafted version). */
  detailFor: (id: string) => string;
  /** Where to open the base template; omit (or return null) to render it as text. */
  baseHref?: (id: string) => string | null;
}

/** A padlock, inline: the icon registry has no lock and this page is its only user. */
function LockIcon() {
  return (
    <svg className={gov.lockIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function FormSchemaEditor({
  schema,
  replace,
  historyState,
  onDeleted,
  toast,
  api,
  detailFor,
  baseHref,
}: FormSchemaEditorProps) {
  const { t } = useTranslation();
  const editable = schema.status === 'DRAFT';
  const institutionScoped = schema.institutionId !== null;
  const [title, setTitle] = useState(schema.title);
  const [description, setDescription] = useState(schema.description ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(schema.effectiveFrom);
  const [changeSummary, setChangeSummary] = useState(schema.changeSummary ?? '');
  const [types, setTypes] = useState<string[]>(schema.applicableInstitutionTypes);
  const [sections, setSections] = useState<FormSection[]>(schema.sections);
  const [fields, setFields] = useState<FormField[]>(schema.fields);
  const [saving, setSaving] = useState(false);
  const [serverReport, setServerReport] = useState<ValidationReport | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [sectionDialog, setSectionDialog] = useState<{ section: FormSection | null } | null>(null);
  const [fieldDialog, setFieldDialog] = useState<{ field: FormField | null; sectionCode: string } | null>(null);
  const [redrafting, setRedrafting] = useState(false);

  useEffect(() => {
    setSections(schema.sections);
    setFields(schema.fields);
  }, [schema]);

  const orderedSections = useMemo(
    () => [...sections].sort((a, b) => a.ordinal - b.ordinal),
    [sections],
  );

  const save = async () => {
    setSaving(true);
    setServerReport(null);
    try {
      const updated = await api.update(schema.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        effectiveFrom,
        applicableInstitutionTypes: types,
        sections,
        fields,
        changeSummary: changeSummary.trim() || undefined,
      });
      replace(updated);
      // The server's report after every save: the same checks gate the move
      // to ACTIVE, so an operator who saves clean can transition without a
      // surprise refusal.
      setServerReport(await api.validate(schema.id));
      toast.success(t('formSchemas.savedToast', { code: updated.code, n: updated.version }));
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  const runValidate = async () => {
    setSaving(true);
    try {
      setServerReport(await api.validate(schema.id));
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setDeleteError(null);
    try {
      await api.remove(schema.id);
      toast.success(t('formSchemas.deletedToast', { code: schema.code, n: schema.version }));
      onDeleted();
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause.message : String(cause));
      setSaving(false);
    }
  };

  const draftNextVersion = async () => {
    setRedrafting(true);
    try {
      const created = await api.create({
        formDomain: schema.formDomain,
        code: schema.code,
        title: schema.title,
        description: schema.description ?? undefined,
        effectiveFrom: schema.effectiveFrom,
        applicableInstitutionTypes: schema.applicableInstitutionTypes,
        sections: schema.sections,
        fields: schema.fields,
        changeSummary: t('acadPolicies.redraftSummary', { n: schema.version }),
        ...(schema.baseSchemaId ? { baseSchemaId: schema.baseSchemaId } : {}),
      });
      toast.success(t('formSchemas.redraftedToast', { code: created.code, n: created.version }));
      window.location.assign(detailFor(created.id));
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : String(cause));
      setRedrafting(false);
    }
  };

  const removeSection = (code: string) => {
    // Removing a section orphans its fields; taking them along is the only
    // reading of the click that does not leave invisible junk in the draft.
    setSections(sections.filter((section) => section.code !== code));
    setFields(fields.filter((field) => field.sectionCode !== code));
  };

  const subject = `${schema.code} v${schema.version}`;
  const baseTarget = schema.baseSchemaId
    ? (baseHref?.(schema.baseSchemaId) ?? null)
    : null;

  return (
    <>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{schema.title}</h1>
          <div className={styles.rowMetaLine}>
            <code className={styles.codeTag}>{schema.code}</code>
            <span className={styles.mutedTag}>{t('grading.versionShort', { n: schema.version })}</span>
            <StateBadge kind="lifecycle" value={schema.status} />
            <span className={gov.scopeTag}>
              {institutionScoped ? t('grading.scopeInstitution') : t('formSchemas.scopePlatform')}
            </span>
            {schema.baseSchemaId &&
              (baseTarget ? (
                <Link className={styles.rowTitleLink} to={baseTarget}>
                  {t('formSchemas.baseLink')}
                </Link>
              ) : (
                <span className={styles.mutedTag}>{t('formSchemas.baseLink')}</span>
              ))}
          </div>
        </div>
        <div className={gov.inlineActions}>
          {editable && (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setDeleting(true)} disabled={saving}>
                {t('formSchemas.deleteDraft')}
              </button>
              <button type="button" className={styles.secondaryBtn} onClick={runValidate} disabled={saving}>
                {t('formSchemas.validate')}
              </button>
              <button type="button" className={styles.primaryBtn} onClick={save} disabled={saving}>
                {t('formSchemas.saveDraft')}
              </button>
            </>
          )}
          {(schema.status === 'ACTIVE' || schema.status === 'SUPERSEDED') && (
            <button type="button" className={styles.primaryBtn} onClick={draftNextVersion} disabled={redrafting}>
              <Icon name="plus" size={16} />
              {t('formSchemas.draftNewVersion')}
            </button>
          )}
        </div>
      </header>

      {schema.status === 'ACTIVE' && (
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('formSchemas.readOnlyActive')}
        </p>
      )}
      {!editable && schema.status !== 'ACTIVE' && (
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {schema.status === 'SUPERSEDED'
            ? t('formSchemas.readOnlySuperseded')
            : t('acadPolicies.readOnlyInReview')}
        </p>
      )}

      {serverReport && (
        <div className={serverReport.valid ? gov.liveReportOk : gov.liveReport} role="status">
          <strong>
            {serverReport.valid
              ? t('grading.serverValid')
              : t('grading.serverInvalid', { total: serverReport.errors.length })}
          </strong>
          {!serverReport.valid && (
            <ul className={gov.liveReportList}>
              {/* The server's sentences, verbatim — protected-floor breaches
                  name the exact field and the base template. */}
              {serverReport.errors.map((sentence) => (
                <li key={sentence}>{sentence}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Panel title={t('acadPolicies.lifecyclePanel')} icon="pulse">
        {schema.lastTransitionReason && (
          <p className={styles.rowDetail}>
            {t('governance.history.reason', { reason: schema.lastTransitionReason })}
          </p>
        )}
        <LifecycleTransitions
          status={schema.status}
          subject={subject}
          activationImpact={t('formSchemas.activateImpact', { code: schema.code })}
          perform={async (to, reason) => {
            const updated = await api.transition(schema.id, to, reason);
            replace(updated);
            toast.success(
              t('governance.transition.doneToast', {
                subject,
                status: t(`platform.state.lifecycle.${to}`),
              }),
            );
          }}
        />
        {schema.status === 'SUPERSEDED' && (
          <p className={styles.sectionEmpty} role="status">
            {t('governance.transition.noMoves')}
          </p>
        )}
      </Panel>

      <Panel title={t('acadPolicies.metadataPanel')} icon="settings">
        <div className={gov.metaGrid}>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="schema-title">{t('formSchemas.fieldTitle')}</label>
            <input id="schema-title" className={gov.editorInput} value={title} disabled={!editable}
              onChange={(event) => setTitle(event.target.value)} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="schema-effective">{t('formSchemas.fieldEffective')}</label>
            <input id="schema-effective" className={gov.editorInputNarrow} type="date" value={effectiveFrom}
              disabled={!editable} onChange={(event) => setEffectiveFrom(event.target.value)} />
          </div>
        </div>
        <div className={gov.fieldStack} style={{ marginTop: '0.75rem' }}>
          <label className={styles.fieldLabel} htmlFor="schema-description">{t('acadPolicies.fieldDescription')}</label>
          <textarea id="schema-description" className={styles.textarea} rows={2} value={description}
            disabled={!editable} onChange={(event) => setDescription(event.target.value)} />
        </div>
        <div className={gov.fieldStack} style={{ marginTop: '0.75rem' }}>
          <label className={styles.fieldLabel} htmlFor="schema-summary">{t('acadPolicies.fieldSummary')}</label>
          <textarea id="schema-summary" className={styles.textarea} rows={2} value={changeSummary}
            disabled={!editable} onChange={(event) => setChangeSummary(event.target.value)} />
        </div>
        <fieldset className={styles.fieldset} style={{ marginTop: '0.75rem' }}>
          <legend className={styles.fieldLabel}>{t('grading.fieldTypes')}</legend>
          <div className={gov.checkboxGrid}>
            {INSTITUTION_TYPES.map((type) => (
              <Checkbox
                key={type}
                label={t(`admin.profile.institutionTypes.${type}`, { defaultValue: type })}
                checked={types.includes(type)}
                disabled={!editable}
                onChange={(event) =>
                  setTypes(
                    event.target.checked ? [...types, type] : types.filter((value) => value !== type),
                  )
                }
              />
            ))}
          </div>
        </fieldset>
      </Panel>

      <div className={gov.previewSideBySide}>
        <Panel
          title={t('formSchemas.builderPanel')}
          icon="checklist"
          actions={
            editable ? (
              <button type="button" className={styles.secondaryBtn} onClick={() => setSectionDialog({ section: null })}>
                <Icon name="plus" size={15} />
                {t('formSchemas.addSection')}
              </button>
            ) : undefined
          }
        >
          {orderedSections.length === 0 && (
            <p className={styles.sectionEmpty} role="status">{t('formSchemas.noSections')}</p>
          )}
          {orderedSections.map((section) => {
            const sectionFields = fields
              .filter((field) => field.sectionCode === section.code)
              .sort((a, b) => a.ordinal - b.ordinal);
            return (
              <section key={section.code} style={{ marginBottom: '1rem' }}>
                <div className={styles.rowMetaLine}>
                  <h3 className={styles.subheading} style={{ margin: 0 }}>
                    {section.ordinal}. {section.title}
                  </h3>
                  <code className={styles.codeTag}>{section.code}</code>
                  {section.conditionalOn && (
                    <span className={styles.infoTag}>
                      {t('formSchemas.conditionalTag', {
                        field: section.conditionalOn.fieldCode,
                        value: String(section.conditionalOn.equals),
                      })}
                    </span>
                  )}
                  {editable && (
                    <span className={gov.inlineActions}>
                      <button type="button" className={styles.linkBtn} onClick={() => setSectionDialog({ section })}>
                        {t('common.edit')}
                      </button>
                      <button type="button" className={styles.linkBtnDanger} onClick={() => removeSection(section.code)}>
                        {t('common.remove')}
                      </button>
                      <button type="button" className={styles.linkBtn}
                        onClick={() => setFieldDialog({ field: null, sectionCode: section.code })}>
                        {t('formSchemas.addField')}
                      </button>
                    </span>
                  )}
                </div>
                {section.description && <p className={styles.rowDetail}>{section.description}</p>}
                {sectionFields.length === 0 ? (
                  <p className={styles.sectionEmpty} role="status">{t('formSchemas.noFields')}</p>
                ) : (
                  <div className={styles.tableWrap}>
                    <table className={gov.editorTable}>
                      <thead>
                        <tr>
                          <th scope="col">{t('formSchemas.colLabel')}</th>
                          <th scope="col">{t('formSchemas.colCode')}</th>
                          <th scope="col">{t('formSchemas.colType')}</th>
                          <th scope="col">{t('formSchemas.colRequired')}</th>
                          <th scope="col">{t('formSchemas.colSensitivity')}</th>
                          {editable && (
                            <th scope="col"><span className={styles.srOnlyText}>{t('common.actions')}</span></th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {sectionFields.map((field) => {
                          // On an institution schema the protected row is the
                          // platform's, not this operator's, to reshape.
                          const locked = field.protected && institutionScoped;
                          return (
                            <tr key={field.code}>
                              <td>
                                {field.protected ? (
                                  <Tooltip content={t('formSchemas.protectedTooltip')}>
                                    <span className={gov.protectedLock}>
                                      <LockIcon />
                                      {field.label}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  field.label
                                )}
                                {field.conditionalOn && (
                                  <span className={styles.infoTag} style={{ marginLeft: '0.4rem' }}>
                                    {t('formSchemas.conditionalTag', {
                                      field: field.conditionalOn.fieldCode,
                                      value: String(field.conditionalOn.equals),
                                    })}
                                  </span>
                                )}
                              </td>
                              <td><code className={styles.codeTag}>{field.code}</code></td>
                              <td>{t(`formSchemas.types.${field.fieldType}`, { defaultValue: field.fieldType })}</td>
                              <td>{field.required ? t('common.yes') : t('common.no')}</td>
                              <td>
                                <span className={`${gov.sensitivityChip} ${gov[`sensitivity_${field.sensitivity}`] ?? ''}`}>
                                  {t(`formSchemas.sensitivities.${field.sensitivity}`, {
                                    defaultValue: field.sensitivity,
                                  })}
                                </span>
                              </td>
                              {editable && (
                                <td>
                                  <span className={gov.inlineActions}>
                                    <button type="button" className={styles.linkBtn}
                                      onClick={() => setFieldDialog({ field, sectionCode: section.code })}>
                                      {t('common.edit')}
                                    </button>
                                    {!locked && (
                                      <button type="button" className={styles.linkBtnDanger}
                                        onClick={() => setFields(fields.filter((f) => f.code !== field.code))}>
                                        {t('common.remove')}
                                      </button>
                                    )}
                                  </span>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })}
        </Panel>

        <Panel title={t('formSchemas.previewPanel')} icon="report">
          <p className={styles.dataNotice}>
            <Icon name="info" size={15} aria-hidden="true" />
            {t('formSchemas.previewNote')}
          </p>
          <FormPreview sections={sections} fields={fields} />
        </Panel>
      </div>

      <Panel title={t('governance.history.title')} icon="history">
        <AsyncPanel state={historyState.state} onRetry={historyState.reload}>
          {(events) => <HistoryTimeline events={events} />}
        </AsyncPanel>
      </Panel>

      {sectionDialog && (
        <SectionDialog
          section={sectionDialog.section}
          sections={sections}
          fields={fields}
          onClose={() => setSectionDialog(null)}
          onSave={(next, originalCode) => {
            if (originalCode) {
              setSections(sections.map((s) => (s.code === originalCode ? next : s)));
              if (originalCode !== next.code) {
                setFields(
                  fields.map((f) =>
                    f.sectionCode === originalCode ? { ...f, sectionCode: next.code } : f,
                  ),
                );
              }
            } else {
              setSections([...sections, next]);
            }
            setSectionDialog(null);
          }}
        />
      )}

      {fieldDialog && (
        <FieldDialog
          field={fieldDialog.field}
          sectionCode={fieldDialog.sectionCode}
          sections={sections}
          fields={fields}
          institutionScoped={institutionScoped}
          onClose={() => setFieldDialog(null)}
          onSave={(next, originalCode) => {
            if (originalCode) {
              setFields(fields.map((f) => (f.code === originalCode ? next : f)));
            } else {
              setFields([...fields, next]);
            }
            setFieldDialog(null);
          }}
        />
      )}

      <ConfirmActionDialog
        open={deleting}
        title={t('formSchemas.deleteTitle', { code: schema.code, n: schema.version })}
        intro={t('formSchemas.deleteIntro')}
        tone="critical"
        impactStatus="ready"
        impact={<p className={styles.rowDetail}>{t('formSchemas.deleteImpact')}</p>}
        confirmPhrase={schema.code}
        confirmLabel={t('formSchemas.deleteDraft')}
        submitting={saving}
        error={deleteError}
        onCancel={() => {
          setDeleting(false);
          setDeleteError(null);
        }}
        onConfirm={() => void remove()}
      />
    </>
  );
}

// ── Section dialog ─────────────────────────────────────────────────────────

function SectionDialog({
  section,
  sections,
  fields,
  onClose,
  onSave,
}: {
  section: FormSection | null;
  sections: FormSection[];
  fields: FormField[];
  onClose: () => void;
  onSave: (next: FormSection, originalCode: string | null) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState(section?.code ?? '');
  const [title, setTitle] = useState(section?.title ?? '');
  const [description, setDescription] = useState(section?.description ?? '');
  const [ordinal, setOrdinal] = useState(
    String(section?.ordinal ?? Math.max(0, ...sections.map((s) => s.ordinal)) + 1),
  );
  const [conditionField, setConditionField] = useState(section?.conditionalOn?.fieldCode ?? '');
  const [conditionValue, setConditionValue] = useState(
    section?.conditionalOn ? String(section.conditionalOn.equals) : '',
  );

  const ordinalNumber = Number.parseInt(ordinal, 10);
  const codeValid = /^[a-zA-Z][a-zA-Z0-9_]*$/.test(code);
  const canSave = codeValid && title.trim().length > 0 && Number.isInteger(ordinalNumber) && ordinalNumber >= 1;

  // A section may only hang off answers already given: fields of sections
  // with a strictly smaller ordinal.
  const targets = eligibleSectionConditionTargets(
    sections.filter((s) => s.code !== section?.code),
    fields,
    Number.isInteger(ordinalNumber) ? ordinalNumber : Number.MAX_SAFE_INTEGER,
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={section ? t('formSchemas.editSection', { code: section.code }) : t('formSchemas.addSection')}
      closeOnBackdropClick={false}
    >
      <div className={styles.dialogForm}>
        <div className={styles.dialogGrid}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="section-code">{t('formSchemas.colCode')}</label>
            <input id="section-code" className={gov.editorInput} value={code} spellCheck={false}
              aria-invalid={code.length > 0 && !codeValid}
              onChange={(event) => setCode(event.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="section-ordinal">{t('formSchemas.colOrdinal')}</label>
            <input id="section-ordinal" className={gov.editorInputNarrow} inputMode="numeric" value={ordinal}
              onChange={(event) => setOrdinal(event.target.value)} />
          </div>
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="section-title">{t('formSchemas.fieldTitle')}</label>
          <input id="section-title" className={gov.editorInput} value={title}
            onChange={(event) => setTitle(event.target.value)} />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="section-description">{t('acadPolicies.fieldDescription')}</label>
          <textarea id="section-description" className={styles.textarea} rows={2} value={description}
            onChange={(event) => setDescription(event.target.value)} />
        </div>
        <ConditionEditor
          idPrefix="section-condition"
          targets={targets}
          fieldCode={conditionField}
          value={conditionValue}
          onFieldChange={setConditionField}
          onValueChange={setConditionValue}
          emptyHint={t('formSchemas.sectionConditionHint')}
        />

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="modal-button"
            disabled={!canSave}
            onClick={() =>
              onSave(
                {
                  code,
                  title: title.trim(),
                  ...(description.trim() ? { description: description.trim() } : {}),
                  ordinal: ordinalNumber,
                  ...(conditionField
                    ? { conditionalOn: { fieldCode: conditionField, equals: conditionValue } }
                    : {}),
                },
                section?.code ?? null,
              )
            }
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Field dialog ───────────────────────────────────────────────────────────

function ConditionEditor({
  idPrefix,
  targets,
  fieldCode,
  value,
  onFieldChange,
  onValueChange,
  emptyHint,
}: {
  idPrefix: string;
  targets: FormField[];
  fieldCode: string;
  value: string;
  onFieldChange: (next: string) => void;
  onValueChange: (next: string) => void;
  emptyHint: string;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.fieldLabel}>{t('formSchemas.conditionLegend')}</legend>
      <p className={styles.fieldHint}>{targets.length === 0 ? emptyHint : t('formSchemas.conditionHint')}</p>
      <div className={styles.dialogGrid}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor={`${idPrefix}-field`}>
            {t('formSchemas.conditionField')}
          </label>
          <Select
            id={`${idPrefix}-field`}
            value={fieldCode}
            onChange={onFieldChange}
            disabled={targets.length === 0}
            options={[
              { value: '', label: t('formSchemas.conditionNone') },
              ...targets.map((target) => ({
                value: target.code,
                label: target.label,
                description: target.code,
              })),
            ]}
          />
        </div>
        {fieldCode && (
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor={`${idPrefix}-value`}>
              {t('formSchemas.conditionValue')}
            </label>
            <input
              id={`${idPrefix}-value`}
              className={gov.editorInput}
              value={value}
              onChange={(event) => onValueChange(event.target.value)}
            />
          </div>
        )}
      </div>
    </fieldset>
  );
}

function FieldDialog({
  field,
  sectionCode,
  sections,
  fields,
  institutionScoped,
  onClose,
  onSave,
}: {
  field: FormField | null;
  sectionCode: string;
  sections: FormSection[];
  fields: FormField[];
  institutionScoped: boolean;
  onClose: () => void;
  onSave: (next: FormField, originalCode: string | null) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState(field?.code ?? '');
  const [label, setLabel] = useState(field?.label ?? '');
  const [section, setSection] = useState(field?.sectionCode ?? sectionCode);
  const [fieldType, setFieldType] = useState<FormFieldType>(field?.fieldType ?? 'TEXT');
  const [ordinal, setOrdinal] = useState(() => {
    if (field) return String(field.ordinal);
    const siblings = fields.filter((f) => f.sectionCode === sectionCode);
    return String(Math.max(0, ...siblings.map((f) => f.ordinal)) + 1);
  });
  const [required, setRequired] = useState(field?.required ?? false);
  const [isProtected, setIsProtected] = useState(field?.protected ?? false);
  const [sensitivity, setSensitivity] = useState<FieldSensitivity>(field?.sensitivity ?? 'INTERNAL');
  const [helpText, setHelpText] = useState(field?.helpText ?? '');
  const [defaultValue, setDefaultValue] = useState(
    field?.defaultValue === undefined || field.defaultValue === null ? '' : String(field.defaultValue),
  );
  const [roleVisibility, setRoleVisibility] = useState((field?.roleVisibility ?? []).join(', '));
  const [minLength, setMinLength] = useState(field?.validation?.minLength?.toString() ?? '');
  const [maxLength, setMaxLength] = useState(field?.validation?.maxLength?.toString() ?? '');
  const [min, setMin] = useState(field?.validation?.min?.toString() ?? '');
  const [max, setMax] = useState(field?.validation?.max?.toString() ?? '');
  const [pattern, setPattern] = useState(field?.validation?.pattern ?? '');
  const [allowedValues, setAllowedValues] = useState((field?.validation?.allowedValues ?? []).join('\n'));
  const [acceptedTypes, setAcceptedTypes] = useState((field?.documentSpec?.acceptedTypes ?? []).join('\n'));
  const [maxSizeMb, setMaxSizeMb] = useState(field?.documentSpec?.maxSizeMb?.toString() ?? '10');
  const [conditionField, setConditionField] = useState(field?.conditionalOn?.fieldCode ?? '');
  const [conditionValue, setConditionValue] = useState(
    field?.conditionalOn ? String(field.conditionalOn.equals) : '',
  );

  // The platform floor, enforced in the editor exactly as the validator will:
  // an institution schema may localise a protected field's label and helpText
  // but never its identity, type, requiredness or sensitivity.
  const locked = institutionScoped && (field?.protected ?? false);

  // The live RegExp compile check — the same check the server runs, moved to
  // the keystroke where the typo happens.
  const patternError = useMemo(() => {
    if (!pattern) return null;
    try {
      new RegExp(pattern);
      return null;
    } catch (cause) {
      return cause instanceof Error ? cause.message : String(cause);
    }
  }, [pattern]);

  const isChoice = fieldType === 'SELECT' || fieldType === 'MULTI_SELECT';
  const isTextual =
    fieldType === 'TEXT' || fieldType === 'TEXT_AREA' || fieldType === 'EMAIL' || fieldType === 'PHONE';
  const isUpload = fieldType === 'FILE_UPLOAD';

  const codeValid = /^[a-zA-Z][a-zA-Z0-9_]*$/.test(code);
  const codeTaken = code !== field?.code && fields.some((f) => f.code === code);
  const ordinalNumber = Number.parseInt(ordinal, 10);
  const canSave =
    codeValid &&
    !codeTaken &&
    label.trim().length > 0 &&
    Number.isInteger(ordinalNumber) &&
    ordinalNumber >= 1 &&
    patternError === null;

  // Strictly earlier fields only — the backend refuses forward references.
  const targets = eligibleConditionTargets(fields, field?.code ?? '');

  const buildValidation = (): FormField['validation'] => {
    const validation: NonNullable<FormField['validation']> = {};
    if (isTextual && minLength) validation.minLength = Number.parseInt(minLength, 10);
    if (isTextual && maxLength) validation.maxLength = Number.parseInt(maxLength, 10);
    if (fieldType === 'NUMBER' && min) validation.min = Number(min);
    if (fieldType === 'NUMBER' && max) validation.max = Number(max);
    if ((isTextual || fieldType === 'NUMBER') && pattern) validation.pattern = pattern;
    if (isChoice) {
      validation.allowedValues = allowedValues
        .split('\n')
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
    }
    return Object.keys(validation).length > 0 ? validation : undefined;
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={field ? t('formSchemas.editField', { code: field.code }) : t('formSchemas.addField')}
      description={locked ? t('formSchemas.protectedTooltip') : undefined}
      closeOnBackdropClick={false}
    >
      <div className={styles.dialogForm}>
        <div className={styles.dialogGrid}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-code">{t('formSchemas.colCode')}</label>
            <input id="field-code" className={gov.editorInput} value={code} spellCheck={false}
              disabled={locked}
              aria-invalid={(code.length > 0 && !codeValid) || codeTaken}
              onChange={(event) => setCode(event.target.value)} />
            {codeTaken && (
              <p className={gov.jsonError} role="alert">{t('formSchemas.codeTaken', { code })}</p>
            )}
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-section">{t('formSchemas.colSection')}</label>
            <Select id="field-section" value={section} disabled={locked}
              onChange={setSection}
              options={sections.map((s) => ({ value: s.code, label: s.title, description: s.code }))} />
          </div>
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="field-label">{t('formSchemas.colLabel')}</label>
          <input id="field-label" className={gov.editorInput} value={label}
            onChange={(event) => setLabel(event.target.value)} />
        </div>
        <div className={styles.dialogGrid}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-type">{t('formSchemas.colType')}</label>
            <Select id="field-type" value={fieldType} disabled={locked}
              onChange={(next) => setFieldType(next as FormFieldType)}
              options={FORM_FIELD_TYPES.map((value) => ({
                value,
                label: t(`formSchemas.types.${value}`, { defaultValue: value }),
              }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-ordinal">{t('formSchemas.colOrdinal')}</label>
            <input id="field-ordinal" className={gov.editorInputNarrow} inputMode="numeric" value={ordinal}
              onChange={(event) => setOrdinal(event.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-sensitivity">{t('formSchemas.colSensitivity')}</label>
            <Select id="field-sensitivity" value={sensitivity} disabled={locked}
              onChange={(next) => setSensitivity(next as FieldSensitivity)}
              options={FIELD_SENSITIVITIES.map((value) => ({
                value,
                label: t(`formSchemas.sensitivities.${value}`, { defaultValue: value }),
                description: t(`formSchemas.sensitivityHints.${value}`),
              }))} />
          </div>
        </div>
        <div className={gov.inlineActions}>
          <Checkbox label={t('formSchemas.colRequired')} checked={required} disabled={locked}
            onChange={(event) => setRequired(event.target.checked)} />
          {/* Only platform templates author protected fields; the validator
              refuses the flag anywhere else, so the checkbox is not offered. */}
          {!institutionScoped && (
            <Checkbox label={t('formSchemas.protectedFlag')} checked={isProtected}
              onChange={(event) => setIsProtected(event.target.checked)} />
          )}
        </div>

        {isTextual && (
          <div className={styles.dialogGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="field-minlength">{t('formSchemas.valMinLength')}</label>
              <input id="field-minlength" className={gov.editorInputNarrow} inputMode="numeric" value={minLength}
                onChange={(event) => setMinLength(event.target.value)} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="field-maxlength">{t('formSchemas.valMaxLength')}</label>
              <input id="field-maxlength" className={gov.editorInputNarrow} inputMode="numeric" value={maxLength}
                onChange={(event) => setMaxLength(event.target.value)} />
            </div>
          </div>
        )}
        {fieldType === 'NUMBER' && (
          <div className={styles.dialogGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="field-min">{t('formSchemas.valMin')}</label>
              <input id="field-min" className={gov.editorInputNarrow} inputMode="decimal" value={min}
                onChange={(event) => setMin(event.target.value)} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="field-max">{t('formSchemas.valMax')}</label>
              <input id="field-max" className={gov.editorInputNarrow} inputMode="decimal" value={max}
                onChange={(event) => setMax(event.target.value)} />
            </div>
          </div>
        )}
        {(isTextual || fieldType === 'NUMBER') && (
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-pattern">{t('formSchemas.valPattern')}</label>
            <p className={styles.fieldHint}>{t('formSchemas.valPatternHint')}</p>
            <input id="field-pattern" className={`${gov.editorInput} ${patternError ? gov.editorInputInvalid : ''}`}
              value={pattern} spellCheck={false}
              aria-invalid={patternError !== null}
              onChange={(event) => setPattern(event.target.value)} />
            {patternError && (
              <p className={gov.jsonError} role="alert">
                {t('formSchemas.valPatternError', { error: patternError })}
              </p>
            )}
          </div>
        )}
        {isChoice && (
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-options">{t('formSchemas.valOptions')}</label>
            <p className={styles.fieldHint}>{t('formSchemas.valOptionsHint')}</p>
            <textarea id="field-options" className={styles.textarea} rows={4} value={allowedValues}
              spellCheck={false} onChange={(event) => setAllowedValues(event.target.value)} />
          </div>
        )}
        {isUpload && (
          <div className={styles.dialogGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="field-accepted">{t('formSchemas.docTypes')}</label>
              <p className={styles.fieldHint}>{t('formSchemas.docTypesHint')}</p>
              <textarea id="field-accepted" className={styles.textarea} rows={3} value={acceptedTypes}
                spellCheck={false} onChange={(event) => setAcceptedTypes(event.target.value)} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="field-maxsize">{t('formSchemas.docMaxSize')}</label>
              <input id="field-maxsize" className={gov.editorInputNarrow} inputMode="decimal" value={maxSizeMb}
                onChange={(event) => setMaxSizeMb(event.target.value)} />
            </div>
          </div>
        )}

        <ConditionEditor
          idPrefix="field-condition"
          targets={targets}
          fieldCode={conditionField}
          value={conditionValue}
          onFieldChange={setConditionField}
          onValueChange={setConditionValue}
          emptyHint={t('formSchemas.fieldConditionHint')}
        />

        <div className={styles.dialogGrid}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-roles">{t('formSchemas.roleVisibility')}</label>
            <p className={styles.fieldHint}>{t('formSchemas.roleVisibilityHint')}</p>
            <input id="field-roles" className={gov.editorInput} value={roleVisibility} spellCheck={false}
              onChange={(event) => setRoleVisibility(event.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="field-default">{t('formSchemas.defaultValue')}</label>
            <input id="field-default" className={gov.editorInput} value={defaultValue}
              onChange={(event) => setDefaultValue(event.target.value)} />
          </div>
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="field-help">{t('formSchemas.helpText')}</label>
          <input id="field-help" className={gov.editorInput} value={helpText}
            onChange={(event) => setHelpText(event.target.value)} />
        </div>

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="modal-button"
            disabled={!canSave}
            onClick={() => {
              const roles = roleVisibility
                .split(',')
                .map((value) => value.trim())
                .filter((value) => value.length > 0);
              const validation = buildValidation();
              onSave(
                {
                  code,
                  sectionCode: section,
                  label: label.trim(),
                  fieldType,
                  ordinal: ordinalNumber,
                  required,
                  protected: institutionScoped ? (field?.protected ?? false) : isProtected,
                  sensitivity,
                  ...(conditionField
                    ? { conditionalOn: { fieldCode: conditionField, equals: conditionValue } }
                    : {}),
                  ...(validation ? { validation } : {}),
                  ...(isUpload
                    ? {
                        documentSpec: {
                          acceptedTypes: acceptedTypes
                            .split('\n')
                            .map((value) => value.trim())
                            .filter((value) => value.length > 0),
                          maxSizeMb: Number(maxSizeMb) || 0,
                        },
                      }
                    : {}),
                  ...(roles.length > 0 ? { roleVisibility: roles } : {}),
                  ...(defaultValue.trim() ? { defaultValue: defaultValue.trim() } : {}),
                  ...(helpText.trim() ? { helpText: helpText.trim() } : {}),
                },
                field?.code ?? null,
              );
            }}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Live preview ───────────────────────────────────────────────────────────

/**
 * The schema rendered as an applicant would meet it — a mock, not a form:
 * answers live in local state, drive conditional visibility through the same
 * pure function the tests pin, and go nowhere. Controls are real (an operator
 * must be able to flip the SELECT that reveals a conditional section) but the
 * upload input is disabled: accepting a real file into a preview would imply
 * it went somewhere.
 */
function FormPreview({ sections, fields }: { sections: FormSection[]; fields: FormField[] }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<string, string | boolean>>({});

  const visibility = useMemo(
    () => evaluateVisibility(sections, fields, answers),
    [sections, fields, answers],
  );

  const orderedSections = [...sections].sort((a, b) => a.ordinal - b.ordinal);
  const setAnswer = (code: string, value: string | boolean) =>
    setAnswers((current) => ({ ...current, [code]: value }));

  if (sections.length === 0) {
    return <p className={styles.sectionEmpty} role="status">{t('formSchemas.previewEmpty')}</p>;
  }

  return (
    <div className={gov.previewPane}>
      {orderedSections
        .filter((section) => visibility.visibleSections.has(section.code))
        .map((section) => (
          <fieldset key={section.code} className={gov.previewSection}>
            <legend className={gov.previewSectionTitle}>{section.title}</legend>
            {section.description && <p className={gov.previewSectionLead}>{section.description}</p>}
            {fields
              .filter(
                (field) =>
                  field.sectionCode === section.code && visibility.visibleFields.has(field.code),
              )
              .sort((a, b) => a.ordinal - b.ordinal)
              .map((field) => (
                <PreviewField
                  key={field.code}
                  field={field}
                  value={answers[field.code]}
                  onChange={(value) => setAnswer(field.code, value)}
                />
              ))}
          </fieldset>
        ))}
    </div>
  );
}

function PreviewField({
  field,
  value,
  onChange,
}: {
  field: FormField;
  value: string | boolean | undefined;
  onChange: (value: string | boolean) => void;
}) {
  const { t } = useTranslation();
  const id = `preview-${field.code}`;
  const text = typeof value === 'string' ? value : '';

  const label = (
    <label className={gov.previewLabel} htmlFor={id}>
      {field.label}
      {field.required && (
        <span className={gov.previewRequired} aria-hidden="true">
          *
        </span>
      )}
    </label>
  );

  let control: React.ReactNode;
  switch (field.fieldType) {
    case 'TEXT_AREA':
      control = (
        <textarea id={id} className={styles.textarea} rows={3} value={text}
          onChange={(event) => onChange(event.target.value)} />
      );
      break;
    case 'NUMBER':
      control = (
        <input id={id} className={gov.editorInputNarrow} inputMode="decimal" value={text}
          onChange={(event) => onChange(event.target.value)} />
      );
      break;
    case 'DATE':
      control = (
        <input id={id} className={gov.editorInputNarrow} type="date" value={text}
          onChange={(event) => onChange(event.target.value)} />
      );
      break;
    case 'SELECT':
      control = (
        <select id={id} className={gov.editorInput} value={text}
          onChange={(event) => onChange(event.target.value)}>
          <option value="">{t('formSchemas.previewChoose')}</option>
          {(field.validation?.allowedValues ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
      break;
    case 'MULTI_SELECT':
      control = (
        <select id={id} className={gov.editorInput} multiple size={Math.min(4, (field.validation?.allowedValues ?? []).length || 2)}
          value={text ? text.split(',') : []}
          onChange={(event) =>
            onChange(
              Array.from(event.target.selectedOptions)
                .map((option) => option.value)
                .join(','),
            )
          }>
          {(field.validation?.allowedValues ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
      break;
    case 'CHECKBOX':
      control = (
        <Checkbox label={field.label} checked={value === true}
          onChange={(event) => onChange(event.target.checked)} />
      );
      break;
    case 'FILE_UPLOAD':
      control = (
        <>
          <input id={id} type="file" disabled aria-describedby={`${id}-spec`} />
          <p className={gov.previewHelp} id={`${id}-spec`}>
            {t('formSchemas.previewUploadSpec', {
              types: (field.documentSpec?.acceptedTypes ?? []).join(', ') || '—',
              size: field.documentSpec?.maxSizeMb ?? '—',
            })}
          </p>
        </>
      );
      break;
    case 'SECTION_REFERENCE':
      control = <p className={gov.previewHelp}>{t('formSchemas.previewSectionReference')}</p>;
      break;
    default:
      // TEXT, EMAIL, PHONE — a plain text control with the right keyboard.
      control = (
        <input
          id={id}
          className={gov.editorInput}
          type={field.fieldType === 'EMAIL' ? 'email' : field.fieldType === 'PHONE' ? 'tel' : 'text'}
          value={text}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }

  return (
    <div className={gov.previewField}>
      {field.fieldType !== 'CHECKBOX' && label}
      {control}
      {field.helpText && <p className={gov.previewHelp}>{field.helpText}</p>}
    </div>
  );
}